import type {
  TransactionFormFragmentFragment,
  UserSelectFragment,
} from '@graphql/__generated__/graphql';
import type {
  FormDefinitionType,
  FormFieldDefinitionType,
} from '@modules/_shared/components/form/RaForm';
import { ReviewTransactionButton } from '@modules/transactions/ReviewTransactionButton';

export type TransactionFormData = Omit<
  TransactionFormFragmentFragment,
  'lot' | 'sellers' | 'buyers'
> & {
  __know_signature_date: boolean;
  sellers_ids: string[];
  buyers_ids: string[];
};

export type TransactionFormContext = {
  readonly: boolean;
  fullAccess: boolean;
  sellerTeamLocked: boolean;
  includeMetadata?: boolean;
  includeSellers?: boolean;
  includeBuyers?: boolean;
  isPurchasePriceRequired?: boolean;
  onUserCreateSelected?: (userData: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  }) => Promise<UserSelectFragment | undefined>;
};

export const transactionFormDefinition: FormDefinitionType<
  TransactionFormData,
  TransactionFormContext
> = ({ t, context }) => {
  const {
    includeSellers = true,
    includeBuyers = true,
    includeMetadata = true,
    isPurchasePriceRequired = true,
  } = context;

  const fieldsDefinition: FormFieldDefinitionType<TransactionFormData>[] = [
    {
      name: 'terms',
      label: t('Terms'),
      type: 'category-title',
    },
    {
      name: 'purchase_price',
      label: t('Purchase price'),
      type: 'number',
      required: isPurchasePriceRequired,
    },
    {
      type: 'custom',
      name: 'reviewButton',
      gridProps: { md: 12 },
      element: ({ id }) => <ReviewTransactionButton transactionId={id} />,
      render: ({ reviewed_at }) => context.fullAccess && reviewed_at == null,
    },
    {
      type: 'checkbox-group',
      name: 'options',
      label: t('Options'),
      checkboxes: [
        {
          name: 'is_published',
          label: t('Publish on RealAdvisor'),
          type: 'checkbox',
          disabled: () => context.readonly,
        },
      ],
      render: ({ reviewed_at }) => context.fullAccess && reviewed_at != null,
      gridProps: { md: 12 },
    },
    {
      name: 'dates',
      label: t('Dates'),
      type: 'category-title',
    },
    {
      name: '__know_signature_date',
      type: 'checkbox',
      label: t('Do you know the signature date?'),
      style: 'switch',
      gridProps: { md: 12 },
    },
    {
      name: 'estimated_signature_date',
      label: t('Estimated signing date'),
      type: 'date',
      required: true,
      render: ({ __know_signature_date }) => !__know_signature_date,
    },
    {
      name: 'signature_date',
      label: t('Signature date'),
      type: 'date',
      required: true,
      render: ({ __know_signature_date }) => __know_signature_date,
    },
    {
      name: 'payment_date',
      label: t('Payment date'),
      type: 'date',
      render: ({ __know_signature_date }) => __know_signature_date,
    },
    {
      name: 'stakeholders',
      label: t('Stakeholders'),
      type: 'category-title',
    },
  ];

  if (includeSellers) {
    fieldsDefinition.push({
      name: 'sellers_ids',
      label: t('Sellers'),
      type: 'user',
      multiple: true,
      createUserSelected: context.onUserCreateSelected,
    });
  }

  if (includeBuyers) {
    fieldsDefinition.push({
      name: 'buyers_ids',
      label: t('Buyers'),
      type: 'user',
      multiple: true,
      createUserSelected: context.onUserCreateSelected,
    });
  }

  fieldsDefinition.push(
    {
      name: 'seller_broker_id',
      label: t('Seller agent'),
      type: 'user',
      required: true,
      filters: { is_broker: { _eq: true } },
      shortlistTeamField: 'seller_broker_team_id',
    },
    {
      name: 'seller_broker_team_id',
      label: t('Seller agent team'),
      type: 'team',
      disabled: () => context.sellerTeamLocked,
    },
    {
      name: 'buyer_broker_id',
      label: t('Buyer agent'),
      type: 'user',
      filters: { is_broker: { _eq: true } },
      shortlistTeamField: 'buyer_broker_team_id',
    },
    {
      name: 'buyer_broker_team_id',
      label: t('Buyer agent team'),
      type: 'team',
    },
  );

  if (includeMetadata) {
    fieldsDefinition.push({
      name: 'meta',
      label: t('Metadata'),
      type: 'category-title',
    });
    fieldsDefinition.push({
      name: 'created_at',
      label: t('Created at'),
      type: 'date',
      disabled: () => true,
    });
    fieldsDefinition.push({
      name: 'created_by',
      label: t('Created by'),
      type: 'user',
      disabled: () => true,
    });
    fieldsDefinition.push({
      name: 'updated_at',
      label: t('Updated at'),
      type: 'date',
      disabled: () => true,
    });
    fieldsDefinition.push({
      name: 'reviewed_at',
      label: t('Approved at'),
      type: 'date',
      disabled: () => true,
    });
    fieldsDefinition.push({
      name: 'reviewed_by',
      label: t('Approved by'),
      type: 'user',
      disabled: () => true,
    });
  }

  return fieldsDefinition;
};
